/* import __COLOCATED_TEMPLATE__ from './convert-ticket-panel.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { TicketTypeResource } from 'embercom/components/inbox2/ticket-type-resource';
import { action } from '@ember/object';
import { useResource } from 'ember-resources';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Snackbar from 'embercom/services/snackbar';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import Conversation from 'embercom/objects/inbox/conversation';
import type InboxApi from 'embercom/services/inbox-api';
import type TicketAttributeSummary from 'embercom/objects/inbox/ticket-attribute-summary';
import type InboxState from 'embercom/services/inbox-state';
import type LinkedTicketsResource from './linked-tickets-resource';
import { tracked } from '@glimmer/tracking';
import type TicketStateService from 'embercom/services/ticket-state-service';
import type Session from 'embercom/services/session';

export interface ConvertTicketPanelArgs {
  originalConversation: Conversation;
  ticketTypeToLinkId?: number;
  hidePanel: () => void;
  linkedTicketsResource: LinkedTicketsResource;
}

interface Signature {
  Args: ConvertTicketPanelArgs;
}

interface SelectedTicketState {
  ticketTypeId: number;
  stateId: number;
}

export default class ConvertTicketPanel extends Component<Signature> {
  @service declare snackbar: Snackbar;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare ticketStateService: TicketStateService;
  @service declare session: Session;

  selectedUserCompanyId: string | null = null;
  @tracked private selectedTicketState?: SelectedTicketState;

  get isTicketStateSelectionEnabled(): boolean {
    return this.session.workspace?.isFeatureEnabled('spg-inbox-convert-to-ticket-state') ?? false;
  }

  get effectiveSelectedStateId(): number | undefined {
    if (!this.isTicketStateSelectionEnabled) {
      return undefined;
    }
    let ticketType = this.ticketTypeResource.selectedTicketType;
    if (!ticketType) {
      return undefined;
    }
    if (this.selectedTicketState?.ticketTypeId === ticketType.id) {
      return this.selectedTicketState.stateId;
    }
    let states = this.ticketStateService.getLiveCustomStatesForTicketType(ticketType);
    return states[0]?.id;
  }

  @action
  handleTicketStateSelected(stateId: number) {
    let ticketType = this.ticketTypeResource.selectedTicketType;
    if (ticketType) {
      this.selectedTicketState = { ticketTypeId: ticketType.id, stateId };
    }
  }

  readonly ticketTypeResource = useResource(this, TicketTypeResource, () => ({
    ticketTypeId: this.args.ticketTypeToLinkId,
    conversation: undefined,
  }));

  @task({ drop: true })
  *convertToTicket(): any {
    if (this.args.ticketTypeToLinkId === undefined) {
      return;
    }

    if (this.ticketTypeResource.missingRequiredAttributes.length > 0) {
      let errorMessage = this.intl.t('inbox.new-conversation.errors.invalid-ticket-attributes', {
        required_attributes: this.ticketTypeResource.missingRequiredAttributes
          .map((attr) => attr.descriptor.name)
          .join(', '),
      });
      this.snackbar.notifyError(errorMessage);
      return;
    }

    if (this.ticketTypeResource.attributesWithInvalidTypes.length > 0) {
      let errorMessage = this.intl.t(
        'inbox.new-conversation.errors.invalid-ticket-attributes-types',
        {
          invalid_attributes: this.ticketTypeResource.attributesWithInvalidTypes
            .map((attr) => attr.descriptor.name)
            .join(', '),
        },
      );
      this.snackbar.notifyError(errorMessage);
      return;
    }

    let ticketAttributes = this.ticketTypeResource.ticketAttributes?.map(
      (attributeSummary: TicketAttributeSummary) => {
        return {
          descriptor_id: attributeSummary.descriptor.id,
          value: attributeSummary.serializedValue,
        };
      },
    );

    try {
      let conversationWire = yield this.inboxApi.createTicketFromConversation(
        this.args.originalConversation,
        this.args.ticketTypeToLinkId,
        ticketAttributes,
        true,
        this.selectedUserCompanyId,
        this.effectiveSelectedStateId,
      );
      let conversation = Conversation.deserialize(conversationWire);
      if (!conversation.ticketType) {
        throw new Error(this.intl.t('inbox.notifications.ticket-creation-failed'));
      }
      let attributes = conversation.attributes;
      this.args.originalConversation.addTicket(conversation.ticketType, attributes);
      this.ticketTypeResource.refreshAttributes();
      if (this.inboxState?.activeConversation?.id === conversation.id) {
        // Update active conversation object with the new converted one ( if we still are in this conversation)
        this.inboxState.activeConversation = conversation;
      }
      this.args.hidePanel();
    } catch (e) {
      let error = (yield e?.response?.json()) as { errors: string };

      let errorText = error?.errors || this.intl.t('inbox.notifications.ticket-creation-failed');
      this.snackbar.notifyError(errorText);
    }
  }

  get isConvertingToTicket() {
    return taskFor(this.convertToTicket).isRunning;
  }

  @action
  handleCompanySelected(companyId: string | null) {
    this.selectedUserCompanyId = companyId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConvertTicketPanel': typeof ConvertTicketPanel;
    'inbox2/convert-ticket-panel': typeof ConvertTicketPanel;
  }
}
